/**
 * Workspace Tools hooks (Wave 3 PR-A) — TanStack Query wrappers around
 * /workspace/tools. Replaces the mock module-level `toolsStore` from
 * `lib/studio-tools-store.ts` with real persistence.
 *
 * Endpoints:
 *   GET    /workspace/tools          — list (filterable by source/active)
 *   GET    /workspace/tools/:id      — single-row detail
 *   POST   /workspace/tools          — register external (mcp/openapi/custom/database)
 *   PUT    /workspace/tools/:id      — partial update + valves
 *   DELETE /workspace/tools/:id      — remove (404 / 409 on builtin)
 *   POST   /workspace/tools/:id/test — exercise handler_config connectivity
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiClient } from './api-client';

export type ToolSource =
  | 'builtin'
  | 'mcp'
  | 'openapi'
  | 'custom'
  | 'database'
  | 'http'
  | 'custom_function';

export type ToolPolicy = 'auto_approve' | 'require_approval' | 'forbidden';

export type ToolScope = 'tenant' | 'system';

export interface WorkspaceToolListRow {
  id: string;
  /** Studio Phase 3E — discriminator: 'tenant' | 'system'. */
  scope: ToolScope;
  name: string;
  displayName: string;
  description: string;
  toolSource: ToolSource;
  namespace: string;
  policy: ToolPolicy;
  isActive: boolean;
  isBuiltin: boolean;
  version: number;
  /** Studio Phase 3 — Q10 categories surface. */
  category: string;
  createdAt: string;
}

export interface WorkspaceToolListResponse {
  data: WorkspaceToolListRow[];
  total: number;
}

export interface WorkspaceToolDetail extends WorkspaceToolListRow {
  organizationId: string | null;
  spec: unknown;
  /** System tools surface IO schemas explicitly (no spec representation). */
  inputSchema: unknown | null;
  outputSchema: unknown | null;
  sideEffects: string[];
  handlerConfig: unknown | null;
  adminValves: unknown | null;
  userValvesSchema: unknown | null;
  updatedAt: string;
}

export interface WorkspaceToolDetailResponse {
  data: WorkspaceToolDetail;
}

const KEYS = {
  list: ['workspace-tools'] as const,
  detail: (id: string) => ['workspace-tools', id] as const,
};

// ─── Reads ──────────────────────────────────────────────────────────────────

export function useWorkspaceTools(opts: {
  source?: ToolSource;
  active?: 'true' | 'false';
  /** 'tenant' | 'system' | 'all' (default 'all'). Studio Phase 3E. */
  scope?: 'tenant' | 'system' | 'all';
  category?: string;
} = {}) {
  const params = new URLSearchParams();
  if (opts.source) params.set('source', opts.source);
  if (opts.active) params.set('active', opts.active);
  if (opts.scope) params.set('scope', opts.scope);
  if (opts.category) params.set('category', opts.category);
  const qs = params.toString();

  return useQuery<WorkspaceToolListResponse, Error>({
    queryKey: [...KEYS.list, opts],
    queryFn: () =>
      apiClient.get<WorkspaceToolListResponse>(
        `/workspace/tools${qs ? `?${qs}` : ''}`,
      ),
  });
}

export function useWorkspaceTool(id: string | null | undefined) {
  return useQuery<WorkspaceToolDetailResponse, Error>({
    queryKey: id ? KEYS.detail(id) : ['workspace-tools', '__none__'],
    queryFn: () =>
      apiClient.get<WorkspaceToolDetailResponse>(`/workspace/tools/${id}`),
    enabled: !!id,
    // fix-wave-4 #974 (STUDIO-ROUTE-012) — Pre-fix the default retry:1
    // delayed surfacing the 404 to the user by ~1s, and on warm reruns
    // it could push past the 30s test budget when stacked with cache
    // revalidation. 4xx responses are deterministic — we never want to
    // retry them.
    retry: (failureCount, err) => {
      // apiClient throws errors whose message starts with the status
      // code; fall back to no-retry on any error that looks like a 4xx
      // (defensive — we'd rather show the error faster than mask a
      // real network blip).
      const msg = (err as Error)?.message ?? '';
      if (/^(4\d\d|404|403|401)\b/.test(msg)) return false;
      return failureCount < 1;
    },
  });
}

// ─── Mutations ──────────────────────────────────────────────────────────────

export interface CreateWorkspaceToolInput {
  name: string;
  displayName: string;
  description: string;
  toolSource: 'mcp' | 'openapi' | 'custom' | 'database';
  /** Tenant tool schema envelope: { input: JSON Schema, output: JSON Schema }. */
  spec: unknown;
  /** Source-specific config: URL, auth, query, etc. */
  handlerConfig: unknown;
  namespace?: string;
  policy?: ToolPolicy;
  sideEffects?: string[];
}

export function useCreateWorkspaceTool() {
  const qc = useQueryClient();
  return useMutation<
    { id: string; name: string },
    Error,
    CreateWorkspaceToolInput
  >({
    mutationFn: (input) =>
      apiClient.post<{ id: string; name: string }>('/workspace/tools', input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export interface UpdateWorkspaceToolInput {
  id: string;
  displayName?: string;
  description?: string;
  policy?: ToolPolicy;
  isActive?: boolean;
  handlerConfig?: unknown;
  adminValves?: unknown;
  userValvesSchema?: unknown;
}

export function useUpdateWorkspaceTool() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, UpdateWorkspaceToolInput>({
    mutationFn: ({ id, ...body }) =>
      apiClient.request<{ success: boolean }>(`/workspace/tools/${id}`, {
        method: 'PUT',
        body: JSON.stringify(body),
        headers: { 'Content-Type': 'application/json' },
      }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: KEYS.list });
      qc.invalidateQueries({ queryKey: KEYS.detail(vars.id) });
    },
  });
}

export function useDeleteWorkspaceTool() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, Error, { id: string }>({
    mutationFn: ({ id }) =>
      apiClient.delete<{ success: boolean }>(`/workspace/tools/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.list });
    },
  });
}

export interface TestWorkspaceToolResult {
  success: boolean;
  latencyMs: number;
  error: string | null;
}

export function useTestWorkspaceTool() {
  return useMutation<TestWorkspaceToolResult, Error, { id: string; input?: unknown }>({
    mutationFn: ({ id, input }) =>
      apiClient.post<TestWorkspaceToolResult>(
        `/workspace/tools/${id}/test`,
        input ? { input } : {},
      ),
  });
}

// ─── Tool ↔ Agent Grants (Phase 5A — #149) ─────────────────────────────────

export interface ToolAgentGrant {
  grantId: string;
  agentId: string;
  agentName: string;
  agentSlug: string | null;
  scope: 'system' | 'tenant';
  notes: string | null;
  grantedAt: string;
}

const GRANT_KEYS = {
  list: (toolId: string) => ['workspace-tools', toolId, 'grants'] as const,
};

export function useToolAgentGrants(toolId: string | null | undefined) {
  return useQuery<{ items: ToolAgentGrant[] }, Error>({
    queryKey: toolId ? GRANT_KEYS.list(toolId) : ['__skip__'],
    queryFn: () => apiClient.get<{ items: ToolAgentGrant[] }>(
      `/workspace/tools/${toolId}/grants`,
    ),
    enabled: !!toolId,
  });
}

export function useGrantToolToAgents(toolId: string) {
  const qc = useQueryClient();
  return useMutation<
    { inserted: number; skipped: number },
    Error,
    { agentIds: string[]; notes?: string }
  >({
    mutationFn: (body) =>
      apiClient.post<{ inserted: number; skipped: number }>(
        `/workspace/tools/${toolId}/grants`,
        body,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GRANT_KEYS.list(toolId) });
    },
  });
}

export function useRevokeToolFromAgent(toolId: string) {
  const qc = useQueryClient();
  return useMutation<{ deleted: number }, Error, { agentId: string }>({
    mutationFn: ({ agentId }) =>
      apiClient.delete<{ deleted: number }>(
        `/workspace/tools/${toolId}/grants/${agentId}`,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: GRANT_KEYS.list(toolId) });
    },
  });
}
